import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

const EpisodeFormList = ({ seasons, currentSeasonId, onSubmit, onSelectSeason }) => {
  const [selectedSeasonId, setSelectedSeasonId] = useState(currentSeasonId || null);
  const [episodes, setEpisodes] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  
  // Para el formulario de episodio
  const [tempId, setTempId] = useState('');
  const [episodeNumber, setEpisodeNumber] = useState(1);
  const [name, setName] = useState('');
  const [runtime, setRuntime] = useState(24);
  const [description, setDescription] = useState('');
  const [poster, setPoster] = useState('');
  const [srtUrl, setSrtUrl] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [fileSize, setFileSize] = useState(0);
  const [mimeType, setMimeType] = useState('video/mp4');

  // Encontrar la temporada seleccionada
  const selectedSeason = seasons.find(s => s.tempId === selectedSeasonId);

  useEffect(() => {
    if (selectedSeasonId) {
      // Cargar episodios de la temporada seleccionada
      const season = seasons.find(s => s.tempId === selectedSeasonId);
      setEpisodes(season?.episodes || []);
    }
  }, [selectedSeasonId, seasons]);

  useEffect(() => {
    // Si se proporciona un ID de temporada actual, selecciónalo
    if (currentSeasonId) {
      setSelectedSeasonId(currentSeasonId);
    } else if (seasons.length > 0) {
      // Si no, selecciona la primera temporada por defecto
      setSelectedSeasonId(seasons[0].tempId);
    }
  }, [currentSeasonId, seasons]);

  const addNewEpisode = () => {
    const newEpisodeId = uuidv4();
    const newEpisode = {
      tempId: newEpisodeId,
      episodeNumber: episodes.length + 1,
      name: `Episodio ${episodes.length + 1}`,
      runtime: 24,
      description: '',
      poster: '',
      srtUrl: '',
      videoUrl: '',
      fileSize: 0,
      mimeType: 'video/mp4'
    };
    
    setEpisodes([...episodes, newEpisode]);
    setEditingIndex(episodes.length);
    resetForm(newEpisode);
  };

  const resetForm = (episode = null) => {
    if (episode) {
      setTempId(episode.tempId || '');
      setEpisodeNumber(parseInt(episode.episodeNumber) || 1);
      setName(episode.name || '');
      setRuntime(parseInt(episode.runtime) || 24);
      setDescription(episode.description || '');
      setPoster(episode.poster || '');
      setSrtUrl(episode.srtUrl || '');
      setVideoUrl(episode.videoUrl || '');
      setFileSize(parseInt(episode.fileSize) || 0);
      setMimeType(episode.mimeType || 'video/mp4');
    } else {
      setTempId('');
      setEpisodeNumber(episodes.length + 1);
      setName(`Episodio ${episodes.length + 1}`);
      setRuntime(24);
      setDescription('');
      setPoster('');
      setSrtUrl('');
      setVideoUrl('');
      setFileSize(0);
      setMimeType('video/mp4');
    }
  };

  const editEpisode = (index) => {
    const episode = episodes[index];
    setEditingIndex(index);
    resetForm(episode);
  };

  const deleteEpisode = (index) => {
    const updatedEpisodes = [...episodes];
    updatedEpisodes.splice(index, 1);
    
    // Renumerar episodios
    const renumberedEpisodes = updatedEpisodes.map((ep, idx) => ({
      ...ep,
      episodeNumber: idx + 1,
      name: ep.name === `Episodio ${ep.episodeNumber}` ? `Episodio ${idx + 1}` : ep.name
    }));
    
    setEpisodes(renumberedEpisodes);
    setEditingIndex(null);
    resetForm();
    
    // Actualizar episodios en la temporada seleccionada
    updateSeasonsWithEpisodes(renumberedEpisodes);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    
    const updatedEpisodes = [...episodes];
    
    const episodeData = {
      tempId: tempId || uuidv4(),
      episodeNumber,
      name,
      runtime,
      description,
      poster,
      srtUrl,
      videoUrl,
      fileSize: parseInt(fileSize, 10) || 0,
      mimeType
    };
    
    if (editingIndex !== null && editingIndex < episodes.length) {
      // Actualizar episodio existente
      updatedEpisodes[editingIndex] = episodeData;
    } else {
      // Añadir nuevo episodio
      updatedEpisodes.push(episodeData);
    }
    
    setEpisodes(updatedEpisodes);
    setEditingIndex(null);
    resetForm();
    
    // Actualizar episodios en la temporada seleccionada
    updateSeasonsWithEpisodes(updatedEpisodes);
  };

  const updateSeasonsWithEpisodes = (updatedEpisodes) => {
    // Esta función actualiza los episodios en el estado de las temporadas
    // Será llamada por el componente padre para obtener el estado completo
    const season = seasons.find(s => s.tempId === selectedSeasonId);
    if (season) {
      season.episodes = updatedEpisodes;
      season.episodeCount = updatedEpisodes.length;
    }
  };

  const handleSeasonChange = (e) => {
    const newSeasonId = e.target.value;
    setSelectedSeasonId(newSeasonId);
    
    if (onSelectSeason) {
      onSelectSeason(newSeasonId);
    }
  };

  const handleComplete = () => {
    // Actualizar una última vez los episodios en las temporadas
    updateSeasonsWithEpisodes(episodes);
    
    // Llamar al callback de submit con todas las temporadas actualizadas
    onSubmit(seasons);
  };

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h3 className="text-lg font-medium text-white">Episodios</h3>
        {seasons.length > 0 ? (
          <div className="mt-2">
            <label htmlFor="seasonSelect" className="block text-sm font-medium text-gray-300">
              Seleccionar Temporada
            </label>
            <select
              id="seasonSelect"
              className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-800 text-gray-300"
              value={selectedSeasonId || ''}
              onChange={handleSeasonChange}
            >
              <option value="" disabled>Selecciona una temporada</option>
              {seasons.map((season) => (
                <option key={season.tempId} value={season.tempId}>
                  {season.title || `Temporada ${season.seasonNumber}`}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <p className="text-sm text-gray-400 mt-2">
            No hay temporadas disponibles. Vuelve al paso anterior y añade temporadas primero.
          </p>
        )}
      </div>

      {selectedSeason && (
        <>
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-md font-medium text-gray-300">
              Episodios para {selectedSeason.title} ({episodes.length})
            </h4>
            <div className="space-x-2">
              <button
                type="button"
                onClick={addNewEpisode}
                className="text-blue-400 hover:text-blue-300"
              >
                + Añadir episodio
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {episodes.map((episode, index) => (
              <div 
                key={episode.tempId} 
                className="p-4 border border-gray-600 rounded-lg bg-gray-800 flex justify-between items-center"
              >
                <div>
                  <h5 className="font-medium text-white">
                    {episode.episodeNumber}. {episode.name}
                  </h5>
                  <p className="text-sm text-gray-300">
                    {episode.description.length > 50 
                      ? `${episode.description.substring(0, 50)}...` 
                      : episode.description || 'Sin descripción'}
                  </p>
                  <p className="text-xs text-gray-400">
                    Duración: {episode.runtime} min
                  </p>
                </div>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => editEpisode(index)}
                    className="text-blue-400 hover:text-blue-300"
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteEpisode(index)}
                    className="text-red-400 hover:text-red-300"
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            ))}

            {episodes.length === 0 && (
              <div className="text-center p-6 border border-dashed border-gray-600 rounded-lg bg-gray-800 text-gray-400">
                No hay episodios añadidos. Haz clic en "Añadir episodio".
              </div>
            )}
          </div>

          {editingIndex !== null && (
            <div className="mt-6 p-4 border border-gray-600 rounded-lg bg-gray-800">
              <h4 className="text-md font-medium mb-4 text-white">
                {editingIndex === episodes.length ? 'Añadir episodio' : `Editar ${episodes[editingIndex]?.name || ''}`}
              </h4>
              
              <form onSubmit={handleFormSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300">Número de episodio</label>
                    <input
                      type="number"
                      value={episodeNumber || 1}
                      onChange={(e) => setEpisodeNumber(parseInt(e.target.value) || 1)}
                      required
                      min="1"
                      className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-gray-300"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300">Nombre</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-gray-300"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300">Descripción</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-gray-300"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300">Duración (minutos)</label>
                    <input
                      type="number"
                      value={runtime || 24}
                      onChange={(e) => setRuntime(parseInt(e.target.value) || 24)}
                      required
                      min="1"
                      className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-gray-300"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300">URL del póster (opcional)</label>
                    <input
                      type="text"
                      value={poster}
                      onChange={(e) => setPoster(e.target.value)}
                      placeholder="https://ejemplo.com/poster.jpg"
                      className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-gray-300"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300">URL del video</label>
                  <input
                    type="text"
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                    required
                    placeholder="https://ejemplo.com/video.mp4"
                    className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-gray-300"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300">URL de subtítulos (SRT)</label>
                  <input
                    type="text"
                    value={srtUrl}
                    onChange={(e) => setSrtUrl(e.target.value)}
                    required
                    placeholder="https://ejemplo.com/subtitulos.srt"
                    className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-gray-300"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300">Tamaño del archivo (bytes)</label>
                    <input
                      type="number"
                      value={fileSize || 0}
                      onChange={(e) => setFileSize(parseInt(e.target.value) || 0)}
                      required
                      min="0"
                      className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-gray-300"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300">Tipo MIME</label>
                    <input
                      type="text"
                      value={mimeType}
                      onChange={(e) => setMimeType(e.target.value)}
                      required
                      placeholder="video/mp4"
                      className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-gray-300"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-2 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setEditingIndex(null);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-600 rounded-md text-gray-300 hover:bg-gray-700"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                  >
                    Guardar Episodio
                  </button>
                </div>
              </form>
            </div>
          )}
        </>
      )}

      <div className="pt-4">
        <button
          type="button"
          onClick={handleComplete}
          className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-md"
        >
          Finalizar
        </button>
      </div>
    </div>
  );
};

export default EpisodeFormList;
